import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FormBuilder, FormGroup } from '@angular/forms';
import { debounceTime, distinctUntilChanged } from 'rxjs';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Moment } from 'moment/moment';
import * as moment from 'moment';
import { AuthorizedUserInfo } from '../../../users/users-page/users-page.types';
import {
  TransactionFilterFormDateKeys,
  TransactionsFilterFormModel,
  TransactionsFilterModel,
} from './transactions-filter-form.types';
import { IKkm } from '../../../../core/modules/retail-places/retail-paces.types';
import { ReportApiService } from '../../../../core/modules/report/report-api.service';
import { ReportTypeModel, ReportTypeParam } from './report-type-filters/report-type-filters.types';
import { IBERICA_REPORT, REPORT_TYPE_FILTERS } from './report-type-filters/report-type-filters.config';

@UntilDestroy()
@Component({
  selector: 'tax-transactions-filter-form',
  templateUrl: 'transactions-filter-form.component.html',
  styleUrls: ['transactions-filter-form.component.sass'],
})
export class TransactionsFilterFormComponent implements OnInit, OnChanges {
  @Input() kkm?: IKkm;
  @Input() set userInfo(userInfo: AuthorizedUserInfo | null) {
    if (!userInfo) return;

    this.reportApiService.fetchLimits(userInfo.organisation.inn)
      .pipe(untilDestroyed(this))
      .subscribe((limits) => {
        this.kkmLimitationDays = limits.limitations?.find(limitation => limitation.kkmsFrom === 1)?.daysTo;
        this.daysPlural = {
          one: `до ${this.kkmLimitationDays} дня`,
          few: `до ${this.kkmLimitationDays} дней`,
          other: `до ${this.kkmLimitationDays} дней`,
        };
      });
  }

  @Output() formSubmitted = new EventEmitter<TransactionsFilterModel>();

  daysPlural = {};
  downloadLink?: string;
  transactionsFilterForm?: FormGroup<TransactionsFilterFormModel>;
  isAllowGetReport = false;
  kkmLimitationDays?: number;
  maxDate?: Date;
  startDate: Moment | null = null;
  isIbericaReportSelected = false;
  isActiveButtonResetIbericaReport = false;

  private readonly DATE_FORMAT = 'YYYY-MM-DDTHH:mm:ss';

  constructor(
    private fb: FormBuilder,
    private reportApiService: ReportApiService,
    private route: ActivatedRoute,
  ) {}

  ngOnInit(): void {
    this.initForm();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('kkm' in changes) {
      if (changes.kkm.firstChange) {
        return;
      }

      const isLastShiftChanged = changes.kkm.currentValue.lastShift?.shiftNumber !== changes.kkm.previousValue?.lastShift?.shiftNumber;
      const isKkmIdChanged = changes.kkm.currentValue.id !== changes.kkm.previousValue?.id;

      if (!isKkmIdChanged && isLastShiftChanged) {
        this.transactionsFilterForm?.patchValue({
          shiftNumber: String(this.kkm?.lastShift?.shiftNumber ?? ''),
        });
        this.submitForm();
      }

      if (isKkmIdChanged) {
        this.transactionsFilterForm?.reset();
        this.transactionsFilterForm?.patchValue({
          shiftNumber: String(this.kkm?.lastShift?.shiftNumber ?? ''),
        });
        this.submitForm();
      }
    }
  }

  resetFilter(): void {
    this.transactionsFilterForm?.patchValue({
      shiftNumber: '',
      startDate: null,
      endDate: null,
      reportType: IBERICA_REPORT,
    }, {
      emitEvent: false,
    });
    this.submitForm();
    this.isAllowGetReport = false;
    this.isActiveButtonResetIbericaReport = false;
  }

  setDate(event: MatDatepickerInputEvent<any, any>, type: TransactionFilterFormDateKeys): void {
    this.transactionsFilterForm?.controls[`${type}`]?.setValue(event.value);
  }

  submitForm(): void {
    if (this.transactionsFilterForm) {
      this.downloadLink = this.getDownloadTransactionsLink();
      const transactionFilter = this.transactionsFilterForm?.getRawValue();
      this.formSubmitted.emit(transactionFilter);
    }
  }

  private getDownloadTransactionsLink(): string {
    const baseUrl = `/api/kkms/${this.kkm?.id}/${!this.transactionsFilterForm?.value.reportType ? 'export-transactions' : 'export-reports'}`;
    const queryParams = this.createQueryParamsForDownloadTransactionsLink();

    return queryParams ? `${baseUrl}?${queryParams}` : baseUrl;
  }

  private initForm(): void {
    const { fiscalId, shiftNumber, reportType, startDate, endDate } = this.route.snapshot.queryParams;
    const haveValueInUrl = [fiscalId, shiftNumber, reportType, startDate, endDate].some(value => Boolean(value));
    this.isIbericaReportSelected = Number(reportType) === ReportTypeParam.IBERICA_REPORT;
    this.isActiveButtonResetIbericaReport = this.isIbericaReportSelected && (!!startDate || !!endDate);

    this.transactionsFilterForm = this.fb.group({
      fiscalId: this.fb.nonNullable.control({
        value: String(fiscalId ?? ''), disabled: !!this.getReportType(Number(reportType)),
      }),
      shiftNumber: this.fb.nonNullable.control(haveValueInUrl ? shiftNumber : String(this.kkm?.lastShift?.shiftNumber ?? '')),
      reportType: [reportType ? this.getReportType(Number(reportType)) : null],
      startDate: [startDate ? moment(startDate) : null],
      endDate: [endDate ? moment(endDate) : null],
    });
    this.maxDate = this.getMaxDate();
    this.submitForm();

    this.listenDateChanges();
    this.listenReportTypeChange();
    this.listenShiftNumberChanges();
  }

  private listenDateChanges(): void {
    this.listenDateChange('startDate');
    this.listenDateChange('endDate');
  }

  private listenReportTypeChange(): void {
    this.transactionsFilterForm?.controls.reportType.valueChanges.pipe(untilDestroyed(this)).subscribe({
      next: (reportType) => {
        this.isIbericaReportSelected = reportType?.value === ReportTypeParam.IBERICA_REPORT;
        this.isAllowGetReport = false;
        this.isActiveButtonResetIbericaReport = false;
        this.maxDate = this.getMaxDate();

        this.transactionsFilterForm?.patchValue({
          shiftNumber: '',
          startDate: null,
          endDate: null,
          ...reportType,
          fiscalId: '',
        }, {
          emitEvent: false,
        });
        this.submitForm();
      },
    });
  }

  private listenShiftNumberChanges(): void {
    this.transactionsFilterForm?.controls.shiftNumber.valueChanges.pipe(
      debounceTime(1000),
      distinctUntilChanged(),
      untilDestroyed(this),
    )
      .subscribe((shiftNumber) => {
        if (shiftNumber) {
          this.transactionsFilterForm?.patchValue({
            startDate: null,
            endDate: null,
          }, {
            emitEvent: false,
          });
        }
        this.submitForm();
      });
  }

  private listenDateChange(value: TransactionFilterFormDateKeys): void {
    this.transactionsFilterForm?.get(value)?.valueChanges.pipe(untilDestroyed(this))
      .subscribe(
        (date) => {
          const startDate = value === 'startDate' ? date : this.transactionsFilterForm?.value.startDate ?? null;
          const endDate = value === 'endDate' ? date : this.transactionsFilterForm?.value.endDate ?? null;

          this.startDate = value === 'startDate' ? date : this.startDate;
          this.isAllowGetReport = this.getReportAvailability(startDate, endDate);
          const isVisibleIbericaReport = Boolean(
            this.isIbericaReportSelected
            && this.route.snapshot.queryParams?.startDate
            && this.route.snapshot.queryParams?.endDate,
          );
          this.isActiveButtonResetIbericaReport = !!startDate || !!endDate || isVisibleIbericaReport;

          this.resetShiftNumber();
        },
      );
  }

  private getMaxDate(): Date {
    const todayEndOfDay = moment().endOf('day');

    return this.isIbericaReportSelected ? todayEndOfDay.subtract(1, 'day').toDate() : todayEndOfDay.toDate();
  }

  private getReportAvailability(startDate: Moment | null, endDate: Moment | null): boolean {
    if (!this.kkmLimitationDays) return false;

    const isAllFieldEntered = Boolean(startDate) && Boolean(endDate);
    const countSelectedDays = moment(endDate).diff(moment(startDate), 'day') + 1;
    const isCountDaysCorrect = countSelectedDays <= this.kkmLimitationDays;
    const isRangeCorrect = moment(endDate).diff(moment(startDate)) >= 0;
    return isAllFieldEntered && isCountDaysCorrect && isRangeCorrect;
  }

  private getReportType(reportTypeParam: ReportTypeParam): ReportTypeModel | null {
    return REPORT_TYPE_FILTERS.find(item => item.value === reportTypeParam) ?? null;
  }

  private createQueryParamsForDownloadTransactionsLink(): string | undefined {
    if (!this.transactionsFilterForm) return;

    const {
      shiftNumber,
      reportType,
      startDate,
      endDate,
    } = this.transactionsFilterForm.getRawValue();

    const params = {
      reportType: reportType?.value ?? undefined,
      shiftNumber: shiftNumber || undefined,
      fromDate: shiftNumber ? undefined : (startDate?.format(this.DATE_FORMAT) ?? undefined),
      toDate: shiftNumber ? undefined : (endDate?.endOf('day').format(this.DATE_FORMAT) ?? undefined),
    };

    const urlParams = Object.entries(params)
      .filter(([_, value]) => value !== undefined)
      .map(([key, value]) => `${key}=${String(value)}`)
      .join('&');

    return urlParams.length > 0 ? urlParams : undefined;
  }

  private resetShiftNumber(): void {
    if (!this.isIbericaReportSelected) {
      this.transactionsFilterForm?.get('shiftNumber')?.reset('', {
        emitEvent: false,
      });
    }
  }
}
